/**
 * @file TableLookFields.tsx
 * @description Оформление таблицы в группе «Цвет и шрифт» перед цветом
 * текста (phase19.16, партия SB120, этап 3б; uiux-sb120-stage3 §3
 * «Таблица», §5; данные db-design-sb120-stage3 §1 `table.head`, `stripe`,
 * `lines`):
 *
 * ```
 * Фон шапки        [Нет ✓][Фон]…[Кнопки]…[⌖ Свой]
 * Полосы через строку [Нет ✓]…[⌖ Свой]
 * Линии   ├●───┤ [ 1 ] px  [Нет 0][1][2]   ↺ как было · 1 px
 *         Между строками 1 px, под шапкой 2 px
 * Цвет линий [Авто: линия ✓]…[⌖ Свой]
 * ```
 *
 * Нет ключа — как было (фона нет, линии 1 / 2 px). Толщина под шапкой —
 * из shared `tableLineWidths`, своё правило здесь не придумываем. Контраст
 * текста к фону шапки и полос — предупреждения shared под полями.
 */

import { DEFAULT_SITE_THEME, SECTION_LOOK_LIMITS, type SectionElementOf } from "@chaiberi/shared";

import { ColorRefField } from "../../../site-fields/ColorRefField";
import { ElementWarningNote } from "../fields/ElementWarningNote";
import { useSectionForm } from "../section-form-context";
import { DividerFields } from "./DividerFields";
import { tableLinesHint } from "../../../../lib/site-builder/element-line";

type Table = SectionElementOf<"table">;

interface Props {
  element: Table;
  onChange: (element: Table) => void;
  path: string;
}

export function TableLookFields({ element, onChange, path }: Props) {
  const { theme } = useSectionForm();
  const colors = (theme ?? DEFAULT_SITE_THEME).colors;
  const set = <K extends "head" | "stripe" | "lines">(key: K, v: Table[K] | undefined) => {
    const next = { ...element };
    if (v === undefined) delete next[key];
    else next[key] = v;
    onChange(next);
  };
  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="flex min-w-0 flex-col gap-1">
        <ColorRefField label="Фон шапки" value={element.head} onChange={(c) => set("head", c)} colors={colors} autoLabel="Нет" />
        <ElementWarningNote path={`${path}.head`} />
      </div>
      <div className="flex min-w-0 flex-col gap-1">
        <ColorRefField label="Полосы через строку" value={element.stripe} onChange={(c) => set("stripe", c)} colors={colors} autoLabel="Нет" />
        <ElementWarningNote path={`${path}.stripe`} />
      </div>
      <DividerFields
        label="Линии"
        colorLabel="Цвет линий"
        value={element.lines}
        onChange={(l) => set("lines", l)}
        range={SECTION_LOOK_LIMITS.tableLines}
        path={`${path}.lines`}
        hint={tableLinesHint(element.lines)}
      />
    </div>
  );
}
